import axios, { type AxiosError } from "axios";
import { defineMessages, useIntl } from "react-intl";
import { useNavigate, useLocation } from "react-router-dom";

import {
  MutationActions,
  useEntityMutation,
  type MutationVariables,
  QueryKeys,
  queryClient,
} from "@/api/core";
import { BoostQueryKeys } from "@/api/boosts";
import { useComposer } from "@/contexts/composer-context";
import { useModalContext } from "@/contexts/modal-context";
import { useApi } from "@/hooks";
import { type Status, statusSchema } from "@/schemas";
import toast from "@/toast";
import { type ApiError } from "@/types/api";
import { removeAlertsRelatedToStatus } from "@/utils/alert";

const messages = defineMessages({
  deleteSuccess: {
    id: "status.delete.success",
    defaultMessage: "You have successfully deleted this Truth.",
  },
  boostPreventsDeletionHeading: {
    id: "status.delete.boost_prevents_deletion.heading",
    defaultMessage: "Boost prevents deletion",
  },
  boostPreventsDeletionFallback: {
    id: "status.delete.boost_prevents_deletion.message",
    defaultMessage: "Cancel your active boost before deleting this Truth.",
  },
  boostPreventsDeletionConfirm: {
    id: "status.delete.boost_prevents_deletion.confirm",
    defaultMessage: "Manage boost",
  },
  boostPreventsDeletionCancel: {
    id: "status.delete.boost_prevents_deletion.cancel",
    defaultMessage: "Not now",
  },
});

interface Variables extends MutationVariables {
  withRedraft?: boolean;
}

type BoostPreventsDeletionPayload = {
  error_code?: string;
  error_message?: string;
  boost_id?: string;
};

function isBoostPreventsDeletionError(
  error: unknown,
): error is AxiosError<BoostPreventsDeletionPayload> {
  if (!axios.isAxiosError(error)) return false;
  if (error.response?.status !== 422) return false;
  const data = error.response.data as BoostPreventsDeletionPayload | undefined;
  return data?.error_code === "BOOST_PREVENTS_DELETION";
}

function useDeleteStatus() {
  const api = useApi();
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useModalContext();
  const { updateComposer } = useComposer("redraft");

  return useEntityMutation({
    action: MutationActions.DELETE,
    globalQueryKey: QueryKeys.statuses.global,
    idAttributeFromVariables: "id",
    mutationFn: (variables: Variables) =>
      api.delete<Status>(`/api/v1/statuses/${variables.id}`),
    onError(error: AxiosError<ApiError>) {
      if (isBoostPreventsDeletionError(error)) {
        const data = error.response?.data as
          | BoostPreventsDeletionPayload
          | undefined;
        const message =
          data?.error_message ||
          intl.formatMessage(messages.boostPreventsDeletionFallback);
        const boostId = data?.boost_id;
        openModal("CONFIRM", {
          heading: intl.formatMessage(messages.boostPreventsDeletionHeading),
          message,
          confirm: intl.formatMessage(messages.boostPreventsDeletionConfirm),
          confirmationTheme: "default",
          cancelText: intl.formatMessage(messages.boostPreventsDeletionCancel),
          onConfirm: () => {
            // The cache may hold a single stale page primed by `status-menu`.
            queryClient.invalidateQueries({ queryKey: BoostQueryKeys.list() });
            navigate(boostId ? `/settings/boosts/${boostId}` : "/settings/boosts");
          },
        });
      }
    },
    onSuccess(response, variables) {
      if (variables.withRedraft) {
        const status = statusSchema.parse(response.data);

        if (variables.withRedraft) {
          openModal("COMPOSE", { composeId: "redraft" });

          updateComposer({
            text: status.text || "",
            poll: status.poll || undefined,
            media: status.media_attachments,
            groupId: status.group?.id,
            inReplyToStatusId: status.in_reply_to_id || undefined,
            embed: status.quote?.id
              ? { type: "status", statusId: status.quote.id }
              : null,
          });
        }
      } else {
        // Only show toast if you are NOT redrafting the status.
        toast.success(intl.formatMessage(messages.deleteSuccess));

        removeAlertsRelatedToStatus(variables.id);

        const path = location.pathname;
        const isOnStatusDetails = !!path.match(/@.+\/posts\/[a-zA-Z0-9]+/g);

        if (isOnStatusDetails) {
          navigate("/");
        }
      }
    },
  });
}

export { useDeleteStatus };
